import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInUser } from '../firebase/auth';
import BackToMarketing from './BackToMarketing';
import { api } from '../utils/api';
import { clientHasDemoAgent } from '../utils/onboardingGate';

export default function Login({ onLogin }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signInUser(form.email, form.password);

      if (result.success) {
        localStorage.setItem('user', JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          ...result.userData
        }));

        if (onLogin) onLogin(result.user, result.userData);

        const uid = result.user.uid;
        if (await clientHasDemoAgent(uid)) {
          navigate('/dashboard');
          return;
        }
        try {
          const me = await api('/api/onboarding/me');
          const hasDemo = me.demo_assistant_id || me.onboardingStage === 'demo_ready' || me.onboardingStage === 'build_requested';
          navigate(hasDemo ? '/dashboard' : '/onboarding');
        } catch {
          navigate('/onboarding');
        }
      } else {
        if (result.requiresVerification) {
          localStorage.setItem('user', JSON.stringify({
            uid: result.user?.uid,
            email: form.email,
            displayName: form.email.split('@')[0]
          }));
          navigate('/verify-email');
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '0.5px solid rgba(255,255,255,0.1)',
  };

  return (
    <div className="min-h-screen w-full flex-1 relative overflow-hidden flex items-center justify-center px-4">
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <BackToMarketing className="px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }} />
      </div>

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/screenshots/appymascot.png.png"
            alt="Appy"
            className="w-14 h-14 object-contain mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <div className="hidden w-14 h-14 rounded-full mx-auto mb-4 items-center justify-center text-white text-sm font-bold" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>A</div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">Appify.AI</h1>
          <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-xl px-4 py-3" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '0.5px solid rgba(239,68,68,0.25)' }}>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={inputStyle}
                placeholder="you@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                  style={inputStyle}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Don&apos;t have an account?{' '}
            <a href="/register" className="text-purple-400 hover:text-purple-300 transition font-medium">
              Sign up here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
